import { useCallback, useEffect, useState } from 'react'
import { Mail, Pencil, Send, ShieldCheck, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { mensajeDeError } from '@/lib/errores'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fecha } from './formato'

/**
 * La cuenta con la que el DUEÑO entra, y las dos salidas cuando no puede.
 *
 * Su cuenta nace con el correo que tecleó el asesor, sin verificar; en una empresa pagada sin demo
 * nadie le dio contraseña. Si el correo estaba mal, quedaba fuera sin remedio. Aquí:
 *   · «Enviar acceso»: le llega un enlace para crear SU contraseña (nadie más la conoce).
 *   · «Corregir correo»: solo mientras el dueño NUNCA haya entrado (el caso del dedazo). Si ya
 *     entró, el cambio es de titular y lo hace soporte con ticket.
 */
// Por qué el asesor no puede corregir el correo aunque el dueño no haya entrado (lo decide el servidor).
const MOTIVO = {
  CORREO_YA_VERIFICADO: 'Ese correo ya se verificó: el dueño lo recibe.',
  NO_ES_CUENTA_DE_CLIENTE: 'Esta cuenta no es solo de un cliente.',
  CUENTA_CON_OTRAS_EMPRESAS: 'Esta cuenta es dueña de otras empresas además de esta.',
  CUENTA_DE_OTRO_ASESOR: 'Esta cuenta tiene cotizaciones de otro asesor.',
}

export default function CuentaDueno({ subscriptionId, confirmar, pedirDatos }) {
  const [cuenta, setCuenta] = useState(null)
  const [trabajando, setTrabajando] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const r = await carteraService.cuentaDueno(subscriptionId)
      setCuenta(r?.data || null)
    } catch {
      setCuenta(null)
    }
  }, [subscriptionId])

  useEffect(() => { cargar() }, [cargar])

  const enviar = async () => {
    const seguir = await confirmar({
      titulo: 'Enviar el acceso al dueño',
      descripcion: `Le llega a ${cuenta?.correo} un enlace para crear su contraseña (vence en 3 días). Si ya tenía una, solo cambia si él usa el enlace.`,
      textoConfirmar: 'Enviar acceso',
    })
    if (!seguir) return
    setTrabajando(true)
    try {
      const r = await carteraService.enviarAccesoDueno(subscriptionId)
      toast.success(`Acceso enviado a ${r?.data?.enviadoA || cuenta?.correo}. Pídele que revise también el spam.`, { duration: 8000 })
      cargar()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo enviar el acceso'))
    } finally { setTrabajando(false) }
  }

  const corregir = async () => {
    const datos = await pedirDatos({
      titulo: 'Corregir el correo del dueño',
      descripcion: `Hoy: ${cuenta?.correo}\nSe cambia en su cuenta, en sus cotizaciones y en su empresa, y le enviamos el acceso al correo nuevo.`,
      campos: [
        { clave: 'correo', etiqueta: 'Correo correcto', tipo: 'email' },
        { clave: 'repetir', etiqueta: 'Repite el correo', tipo: 'email' },
      ],
      textoConfirmar: 'Corregir y enviar acceso',
    })
    if (!datos) return
    const nuevo = datos.correo.trim().toLowerCase()
    if (nuevo !== datos.repetir.trim().toLowerCase()) {
      toast.error('Los dos correos no coinciden.')
      return
    }
    setTrabajando(true)
    try {
      const r = (await carteraService.corregirCorreoDueno(subscriptionId, nuevo))?.data || {}
      if (r.acceso?.enviadoA) {
        toast.success(`Correo corregido. Le enviamos el acceso a ${r.acceso.enviadoA}.`, { duration: 8000 })
      } else if (r.acceso?.error) {
        toast(`Correo corregido, pero el acceso no se envió (${r.acceso.error}). Usa «Enviar acceso».`, { icon: '⚠️', duration: 10000 })
      } else {
        toast.success('Correo corregido.')
      }
      cargar()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo corregir el correo'), { duration: 10000 })
    } finally { setTrabajando(false) }
  }

  if (!cuenta) return null

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50 flex items-center gap-1.5">
            <Mail size={14} /> Cuenta del dueño
          </p>
          <p className="text-body-sm text-neutral-800 dark:text-neutral-200 break-all font-mono">{cuenta.correo || '—'}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {cuenta.nuncaEntro
              ? <Badge variant="warning">Nunca ha entrado</Badge>
              : <Badge variant="success">Último acceso: {fecha(cuenta.ultimoAcceso)}</Badge>}
            {cuenta.correoVerificado
              ? <Badge variant="secondary"><ShieldCheck size={12} className="mr-1" /> Correo verificado</Badge>
              : <Badge variant="secondary">Correo sin verificar</Badge>}
            {cuenta.enlaceVigenteHasta && (
              <span className="text-micro text-neutral-500 dark:text-neutral-400">
                Enlace de acceso vigente hasta {fecha(cuenta.enlaceVigenteHasta)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={enviar} disabled={trabajando || !cuenta.correo}>
            <Send size={15} className="mr-1.5" /> Enviar acceso
          </Button>
          {cuenta.puedeCorregirAsesor && (
            <Button variant="outline" onClick={corregir} disabled={trabajando}>
              <Pencil size={15} className="mr-1.5" /> Corregir correo
            </Button>
          )}
        </div>
      </div>
      {cuenta.nuncaEntro && !cuenta.puedeCorregirAsesor ? (
        <p className="text-caption text-neutral-500 dark:text-neutral-400 flex items-start gap-1.5">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {MOTIVO[cuenta.motivoNoCorregible] || 'El correo no se puede corregir desde aquí.'} Si hay que cambiarlo, pídelo a soporte.
        </p>
      ) : cuenta.nuncaEntro ? (
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          Si el correo está mal escrito o el dueño ya no usa ese buzón, corrígelo: como todavía no ha entrado,
          nadie más depende de él. Le llegará un enlace para crear su contraseña.
        </p>
      ) : (
        <p className="text-caption text-neutral-500 dark:text-neutral-400 flex items-start gap-1.5">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          El dueño ya entró con este correo. Si perdió acceso a él, pide el cambio a soporte: lo hace un
          administrador con un ticket, porque cambiar el correo entrega el control de la empresa.
        </p>
      )}
    </div>
  )
}
